// components/tracker/ghost.tsx
//
// The tracker's shared loading skeleton.
//
// GHOSTS CARRY NO NUMBERS, AND THAT IS THE WHOLE RULE. A skeleton showing
// plausible digits is how a placeholder gets read as a finding — this codebase
// has already shipped a dashboard whose Upcoming Events section said
// "0 READOUTS" for the first two seconds of every load, which is not a
// placeholder, it is a false statement about the member's watch. Everything
// here is bars at the REAL row geometry, so the frame is honest while it
// loads and nothing jumps when the data lands.
//
// ONE TREATMENT, SEVEN SECTIONS. Before this existed, each section improvised:
// the sponsors card had a text line, the table had a different text line, the
// chip row and the activity feed rendered nothing at all. Lifting the activity
// leaderboard's ghost rows into a primitive is what makes the loading frame
// read as one page loading rather than seven components disagreeing.
//
// THE STYLESHEET IS THE ONE SHARED MODULE IN `components/tracker`. Consumers
// import THIS COMPONENT; they never import `ghost.module.css` and never
// `composes:` its classes into their own module. Pass layout (position, width,
// margins) via `className` from the calling component's own module — the ghost
// owns the material, the caller owns where it sits.
//
// MOTION IS OPT-OUT, NOT OPT-IN. The 1.6s sheen is in the stylesheet with a
// `prefers-reduced-motion: reduce` guard that drops it to static bars. Do not
// reintroduce the animation from JS, which would route around that guard.
//
// ACCESSIBILITY: every ghost is `aria-hidden`. The signal a screen reader
// needs is `aria-busy="true"` on the SECTION, which is the caller's job —
// announcing "bar bar bar" is worse than announcing nothing.

import type { CSSProperties, ReactNode } from "react"

import styles from "@/components/tracker/ghost.module.css"

/** Which ground the ghost sits on: ink-on-white, or white-on-#17181B. */
export type GhostTone = "light" | "dark"

type Size = number | string

type Base = {
  tone?: GhostTone
  /** Layout from the CALLER's own module. Never a class from ghost.module.css. */
  className?: string
  style?: CSSProperties
}

const px = (v: Size | undefined): string | undefined =>
  v === undefined ? undefined : typeof v === "number" ? `${v}px` : v

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ")
}

/**
 * The base shape. `w` / `h` / `r` are the real measurements of whatever this
 * stands in for — a 10px block under a 10px line, not a generic grey box.
 */
function Block({
  w,
  h = 10,
  r,
  tone = "light",
  className,
  style,
}: Base & { w?: Size; h?: Size; r?: Size }) {
  return (
    <span
      aria-hidden="true"
      className={cx(styles.block, tone === "dark" && styles.dark, className)}
      style={{
        width: px(w),
        height: px(h),
        borderRadius: px(r),
        ...style,
      }}
    />
  )
}

/** The lighter, thinner secondary line — a subtitle under a title. */
function Bar({ w, h, tone = "light", className, style }: Base & { w?: Size; h?: Size }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        styles.block,
        styles.bar,
        tone === "dark" && styles.dark,
        className,
      )}
      style={{ width: px(w), height: px(h), ...style }}
    />
  )
}

/** A square-ish logo or drug mark. Square by default; pass `r` for a disc. */
function Avatar({
  size = 18,
  r,
  tone = "light",
  className,
  style,
}: Base & { size?: Size; r?: Size }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        styles.block,
        styles.avatar,
        tone === "dark" && styles.dark,
        className,
      )}
      style={{
        width: px(size),
        height: px(size),
        borderRadius: px(r),
        ...style,
      }}
    />
  )
}

/** A pill: the count chips, the drug chips, anything with a 999px radius. */
function Chip({
  w = 90,
  h = 30,
  tone = "light",
  className,
  style,
}: Base & { w?: Size; h?: Size }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        styles.block,
        styles.chip,
        tone === "dark" && styles.dark,
        className,
      )}
      style={{ width: px(w), height: px(h), ...style }}
    />
  )
}

/** A hairline-separated list row: title line, then subline. */
function Row({
  children,
  tone = "light",
  className,
  style,
}: Base & { children?: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      className={cx(styles.row, tone === "dark" && styles.rowDark, className)}
      style={style}
    >
      {children}
    </div>
  )
}

/**
 * The avatar-plus-title line inside a `Ghost.Row`.
 *
 * IT TAKES `tone` LIKE EVERY OTHER SHAPE. It used to accept the prop through
 * `Base` and then drop it, so `<Ghost.RowTop tone="dark">` compiled and did
 * nothing — the kind of silent no-op that gets diagnosed as a broken ghost.
 * It carries no fill of its own, so the only thing tone can reach is the
 * hairline a caller may give it through `className`; that is now wired.
 */
function RowTop({
  children,
  tone = "light",
  className,
  style,
}: Base & { children?: ReactNode }) {
  return (
    <div
      className={cx(styles.rowTop, tone === "dark" && styles.rowTopDark, className)}
      style={style}
    >
      {children}
    </div>
  )
}

export const Ghost = { Block, Bar, Avatar, Chip, Row, RowTop }
